/** Store → Orders list with status tabs and search. */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, DatePicker, Dropdown, Empty, Input, Modal, Select, Space, Table, Tabs, Tag, Typography, Upload, message } from 'antd';
import { CarOutlined, FilterOutlined, FolderOutlined, ImportOutlined, PrinterOutlined, TagsOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { directUpload } from './api';
import { Hint, StatusTag, fmtDate, listOf, money, sitePath, toastErr, totalOf, useSite } from './common';
import { CarrierSelect, openDocument, useCarriers } from './ShopLogistics';
import { ExportOrdersButton } from './ShopExports';
import { MoneyInput, countryLabel } from './ShopCommon';

const { Text } = Typography;

export const orderName = (o, shop) => (o?.name || (o?.number ? `${shop?.order_prefix ?? '#'}${o.number}` : `#${o?.id}`));

const TABS = [
    { key: 'any', label: 'All' }, { key: 'unfulfilled', label: 'Unfulfilled' }, { key: 'paid', label: 'Paid' },
    { key: 'open', label: 'Open' }, { key: 'closed', label: 'Closed' }, { key: 'cancelled', label: 'Cancelled' }, { key: 'abandoned', label: 'Abandoned checkouts' },
];

const FILTER_KEYS = ['date_from', 'date_to', 'tag', 'country', 'financial_status', 'fulfillment_status', 'source', 'total_min', 'total_max', 'archived'];
const SOURCE_LABELS = { web: 'Online store', draft: 'Draft order', manual: 'Manual order', import: 'Imported', marketing: 'Email marketing' };

const FilterBar = ({ f, setF, range, setRange, options, currency }) => {
    const { t } = useTranslation();
    const set = (patch) => setF((x) => ({ ...x, ...patch, page: 1 }));
    return (
        <Space wrap style={{ marginBottom: 12 }}>
            <DatePicker.RangePicker value={range} onChange={(v, s) => { setRange(v); set({ date_from: s?.[0] || '', date_to: s?.[1] || '' }); }} />
            <Select allowClear showSearch style={{ width: 130 }} placeholder={t('Tag')} value={f.tag || undefined} onChange={(v) => set({ tag: v || '' })} options={listOf(options.tags).map((x) => { const name = typeof x === 'string' ? x : x.tag ?? x.name; return { value: name, label: name }; })} />
            <Select allowClear showSearch optionFilterProp="label" style={{ width: 150 }} placeholder={t('Country')} value={f.country || undefined} onChange={(v) => set({ country: v || '' })} options={listOf(options.countries).map((c) => ({ value: c, label: countryLabel(c, t) }))} />
            <Select allowClear style={{ width: 150 }} placeholder={t('Payment')} value={f.financial_status || undefined} onChange={(v) => set({ financial_status: v || '' })}
                options={[['pending', 'Pending'], ['paid', 'Paid'], ['partially_refunded', 'Partially refunded'], ['refunded', 'Refunded']].map(([value, l]) => ({ value, label: t(l, { context: 'order' }) }))} />
            <Select allowClear style={{ width: 150 }} placeholder={t('Fulfillment')} value={f.fulfillment_status || undefined} onChange={(v) => set({ fulfillment_status: v || '' })}
                options={[['unfulfilled', 'Unfulfilled'], ['partial', 'Partially fulfilled'], ['fulfilled', 'Fulfilled']].map(([value, l]) => ({ value, label: t(l, { context: 'order' }) }))} />
            <Select allowClear style={{ width: 150 }} placeholder={t('Source')} value={f.source || undefined} onChange={(v) => set({ source: v || '' })}
                options={Object.entries(SOURCE_LABELS).map(([value, l]) => ({ value, label: t(l) }))} />
            <MoneyInput style={{ width: 110 }} currency={currency} placeholder={t('Min total')} value={f.total_min === '' ? null : f.total_min} onChange={(v) => set({ total_min: v ?? '' })} />
            <MoneyInput style={{ width: 110 }} currency={currency} placeholder={t('Max total')} value={f.total_max === '' ? null : f.total_max} onChange={(v) => set({ total_max: v ?? '' })} />
            <Select style={{ width: 150 }} value={f.archived || 'no'} onChange={(v) => set({ archived: v })}
                options={[{ value: 'no', label: t('Not archived') }, { value: 'only', label: t('Archived') }, { value: 'include', label: t('All orders') }]} />
        </Space>
    );
};

const BulkTagModal = ({ mode, onClose, onSubmit, tags }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState([]);
    useEffect(() => { if (mode) setValue([]); }, [mode]);
    return (
        <Modal open={!!mode} onCancel={onClose} onOk={() => onSubmit(value)} okButtonProps={{ disabled: !value.length }} destroyOnHidden
            title={mode === 'remove_tags' ? t('Remove tags') : t('Add tags')}>
            <Select mode="tags" style={{ width: '100%' }} value={value} onChange={setValue} options={tags.map((x) => ({ value: x, label: x }))} />
        </Modal>
    );
};

const BulkFulfillModal = ({ open, orders, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api, shop } = useSite();
    const carriers = useCarriers(api);
    const [rows, setRows] = useState([]);
    const [notify, setNotify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    useEffect(() => { if (open) { setRows(orders.map((o) => ({ order_id: o.id, name: orderName(o, shop), carrier: undefined, tracking_number: '' }))); setResult(null); } }, [open, orders, shop]);
    const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post('/orders/bulk-fulfill', { orders: rows.map(({ order_id, carrier, tracking_number }) => ({ order_id, carrier: carrier || '', tracking_number })), notify });
            const bad = listOf(r?.results).filter((x) => !x.ok);
            message.success(t('{{n}} orders fulfilled', { n: r?.fulfilled ?? rows.length - bad.length }));
            if (bad.length) setResult(bad); else onDone();
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={result ? onDone : onClose} title={t('Fulfill orders')} onOk={result ? onDone : submit} okText={result ? t('Done') : t('Fulfill')} confirmLoading={busy} width={720} destroyOnHidden>
            {result ? (
                <Alert type="warning" showIcon message={t('Some orders were not fulfilled')}
                    description={result.map((x) => <div key={x.order_id}>{rows.find((r) => r.order_id === x.order_id)?.name || x.order_id}: {x.error}</div>)} />
            ) : (
                <>
                    <Table size="small" rowKey="order_id" pagination={false} dataSource={rows} scroll={{ y: 360 }} columns={[
                        { title: t('Order', { context: 'shop' }), dataIndex: 'name', width: 100 },
                        { title: t('Carrier'), key: 'c', width: 220, render: (_, r, i) => <CarrierSelect carriers={carriers} value={r.carrier} onChange={(carrier) => set(i, { carrier })} style={{ width: '100%' }} size="small" /> },
                        { title: t('Tracking number'), key: 'n', render: (_, r, i) => <Input size="small" value={r.tracking_number} onChange={(e) => set(i, { tracking_number: e.target.value.trim() })} /> },
                    ]} />
                    <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ marginTop: 12 }}>{t('Send shipping confirmation to the customer')}</Checkbox>
                </>
            )}
        </Modal>
    );
};

const ImportTrackingModal = ({ open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [file, setFile] = useState(null);
    const [notify, setNotify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    useEffect(() => { if (open) { setFile(null); setRes(null); } }, [open]);
    const submit = async () => {
        if (!file) return;
        setBusy(true);
        try { const { key } = await directUpload(api, file, 'import'); setRes(await api.post('/orders/tracking-import', { key, notify })); onDone(); } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const errors = listOf(res?.errors);
    return (
        <Modal open={open} onCancel={onClose} title={t('Import tracking numbers')} onOk={res ? onClose : submit} okText={res ? t('Done') : t('Import')}
            okButtonProps={{ disabled: !res && !file }} confirmLoading={busy} destroyOnHidden>
            {res ? (
                <Alert type={errors.length ? 'warning' : 'success'} showIcon
                    message={t('{{n}} orders fulfilled', { n: res.fulfilled ?? listOf(res.results).filter((x) => x.ok).length })}
                    description={errors.length ? errors.slice(0, 20).map((e, i) => <div key={i}>{typeof e === 'string' ? e : `${e.row ? `#${e.row} ` : ''}${e.error || e.message || JSON.stringify(e)}`}</div>) : null} />
            ) : (
                <>
                    <Upload.Dragger accept=".csv,text/csv" maxCount={1} beforeUpload={(f) => { setFile(f); return false; }} onRemove={() => setFile(null)} fileList={file ? [file] : []}>
                        <p style={{ margin: 0 }}>{t('CSV file')}<Hint title={t('Columns: Order, Carrier, Tracking Number, Tracking URL')} /></p>
                    </Upload.Dragger>
                    <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ marginTop: 12 }}>{t('Send shipping confirmation to the customer')}</Checkbox>
                </>
            )}
        </Modal>
    );
};

const Orders = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId, currency, shop } = useSite();
    const [f, setF] = useState({ status: 'any', q: '', page: 1, per_page: 25 }) // the API's default without status = paid only;
    const [q, setQ] = useState('');
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState([]);
    const [modal, setModal] = useState(null);
    const [tick, setTick] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [range, setRange] = useState(null);
    const [options, setOptions] = useState({ tags: [], countries: [] });
    const [tagMode, setTagMode] = useState(null);
    const activeFilters = FILTER_KEYS.filter((k) => f[k] !== undefined && f[k] !== '' && !(k === 'archived' && f[k] === 'no')).length;
    const clearFilters = () => { setRange(null); setF((x) => ({ status: x.status, q: x.q, page: 1, per_page: x.per_page })); };
    const tagNames = listOf(options.tags).map((x) => (typeof x === 'string' ? x : x.tag ?? x.name));
    const exportParams = Object.fromEntries(Object.entries({ ...f, filtered: 1, archived: f.archived || 'no', page: undefined, per_page: undefined }).filter(([, v]) => v !== '' && v !== undefined));
    const bulk = async (action, tags) => {
        try {
            const r = await api.post('/orders/bulk', { ids: sel, action, tags });
            message.success(t('{{n}} orders updated', { n: r?.updated ?? sel.length }));
            setTagMode(null); setSel([]); setTick((x) => x + 1);
        } catch (e) { toastErr(e); }
    };

    useEffect(() => {
        let live = true;
        api.get('/orders/filter-options').then((d) => live && setOptions(d || {})).catch(() => {});
        return () => { live = false; };
    }, [api, tick]);

    useEffect(() => {
        let live = true;
        setLoading(true);
        api.get('/orders', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')))
            .then((d) => live && setData({ items: listOf(d), total: totalOf(d) }))
            .catch((e) => { if (live) { if (e.status !== 404) toastErr(e); setData({ items: [], total: 0 }); } })
            .finally(() => live && setLoading(false));
        return () => { live = false; };
    }, [api, f, tick]);

    return (
        <Card title={t('Orders')} styles={{ body: { paddingTop: 0 } }}
            extra={<Space><ExportOrdersButton params={exportParams} /><Button icon={<ImportOutlined />} onClick={() => setModal('import')}>{t('Import tracking')}</Button></Space>}>
            <Tabs activeKey={f.status} onChange={(k) => setF((x) => ({ ...x, status: k, page: 1 }))} items={TABS.map((x) => ({ key: x.key, label: t(x.label, x.key === 'open' ? { context: 'order' } : undefined) }))} />
            <Input.Search allowClear placeholder={t('Search order number, email, name')} value={q} onChange={(e) => setQ(e.target.value)}
                onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} style={{ maxWidth: 320, marginBottom: 12, marginRight: 8 }} />
            <Button icon={<FilterOutlined />} type={showFilters || activeFilters ? 'primary' : 'default'} ghost={!!(showFilters || activeFilters)} onClick={() => setShowFilters((x) => !x)}>{t('Filters')}{activeFilters ? ` (${activeFilters})` : ''}</Button>
            {activeFilters > 0 && <Button type="link" onClick={clearFilters}>{t('Clear')}</Button>}
            {showFilters && <div><FilterBar f={f} setF={setF} range={range} setRange={setRange} options={options} currency={currency} /></div>}
            {sel.length > 0 && (
                <Space style={{ marginBottom: 12, marginLeft: 12 }} wrap>
                    <Text type="secondary">{t('{{n}} selected', { n: sel.length })}</Text>
                    <Button size="small" icon={<CarOutlined />} onClick={() => setModal('fulfill')}>{t('Fulfill')}</Button>
                    <Dropdown menu={{
                        items: [{ key: 'packing_slip', label: t('Packing slips') }, { key: 'invoice', label: t('Invoices') }],
                        onClick: ({ key }) => openDocument(siteId, '/orders/documents', { ids: sel.join(','), kind: key }),
                    }}>
                        <Button size="small" icon={<PrinterOutlined />}>{t('Print')}</Button>
                    </Dropdown>
                    <Dropdown menu={{
                        items: [{ key: 'add_tags', label: t('Add tags') }, { key: 'remove_tags', label: t('Remove tags') }],
                        onClick: ({ key }) => setTagMode(key),
                    }}>
                        <Button size="small" icon={<TagsOutlined />}>{t('Tags')}</Button>
                    </Dropdown>
                    {f.archived === 'only'
                        ? <Button size="small" icon={<FolderOutlined />} onClick={() => bulk('unarchive')}>{t('Unarchive')}</Button>
                        : <Button size="small" icon={<FolderOutlined />} onClick={() => bulk('archive')}>{t('Archive')}</Button>}
                </Space>
            )}
            <Table
                rowKey="id"
                loading={loading}
                rowSelection={{ selectedRowKeys: sel, onChange: setSel, columnWidth: 36 }}
                dataSource={data.items}
                scroll={{ x: 820 }}
                onRow={(o) => ({ onClick: (e) => { if (!e.target.closest('.ant-table-selection-column')) navigate(sitePath(siteId, `orders/${o.id}`)); }, style: { cursor: 'pointer' } })}
                pagination={{ current: f.page, pageSize: f.per_page, total: data.total, hideOnSinglePage: true, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                columns={[
                    { title: t('Order', { context: 'shop' }), key: 'n', width: 110, render: (_, o) => <><a>{orderName(o, shop)}</a>{o.archived && <Tag style={{ marginLeft: 6 }}>{t('Archived')}</Tag>}</> },
                    { title: t('Date'), dataIndex: 'created_at', width: 170, render: (v) => fmtDate(v) },
                    {
                        title: t('Customer'), key: 'c', render: (_, o) => (
                            <div>
                                <div>{o.customer_name || o.shipping_address?.name || o.customer?.name || o.email}</div>
                                {(o.customer_name || o.shipping_address?.name) && <Text type="secondary" style={{ fontSize: 12 }}>{o.email}</Text>}
                            </div>
                        ),
                    },
                    { title: t('Payment'), dataIndex: 'financial_status', width: 140, render: (v, o) => <Space size={4}><StatusTag value={v} />{o.status === 'cancelled' && <StatusTag value="cancelled" />}</Space> },
                    { title: t('Fulfillment'), dataIndex: 'fulfillment_status', width: 150, render: (v) => <StatusTag value={v} /> },
                    { title: t('Items'), key: 'items', width: 80, render: (_, o) => o.items_count ?? o.item_count ?? (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0) },
                    { title: t('Total', { context: 'shop' }), key: 'total', width: 120, align: 'right', render: (_, o) => (<>{money(o.total, o.currency || currency)}{o.base_currency && o.currency && o.base_currency !== o.currency && o.base_total != null ? <div style={{ fontSize: 11, opacity: 0.6 }}>≈ {money(o.base_total, o.base_currency)}</div> : null}</>) },
                ]}
                locale={{ emptyText: <Empty description={f.q || f.status !== 'any' ? t('No orders match') : t('No orders yet')} /> }}
            />
            <BulkFulfillModal open={modal === 'fulfill'} orders={data.items.filter((o) => sel.includes(o.id))} onClose={() => setModal(null)}
                onDone={() => { setModal(null); setSel([]); setTick((x) => x + 1); }} />
            <BulkTagModal mode={tagMode} tags={tagNames} onClose={() => setTagMode(null)} onSubmit={(tags) => bulk(tagMode, tags)} />
            <ImportTrackingModal open={modal === 'import'} onClose={() => setModal(null)} onDone={() => setTick((x) => x + 1)} />
        </Card>
    );
};

export default Orders;
